(() => {
  const elementName = 'abok-sticky-add-to-cart';
  if (customElements.get(elementName)) return;

  class AbokStickyAddToCart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.mediaQuery = window.matchMedia('(max-width: 749px)');
      this.isBusy = false;
      this.isEditorForced = false;
      this.hasPassedPrimary = this.dataset.visibilityMode === 'always';
      this.footerIsNear = false;
      this.cookieIsVisible = false;

      this.cacheElements();
      this.resolvePrimaryButton();
      this.bindEvents();
      this.observePrimaryButton();
      this.observeVisibility();
      this.observeFooter();
      this.observeCookieConsent();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }

      this.hidden = false;
      this.syncPrimaryState();
      this.updateVisibility();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.variantUnsubscriber?.();
      this.primaryStateObserver?.disconnect();
      this.primaryVisibilityObserver?.disconnect();
      this.footerObserver?.disconnect();
      this.cookieObserver?.disconnect();
      this.cookieDiscoveryObserver?.disconnect();
      this.mediaQuery?.removeEventListener?.('change', this.handleMediaChange);
      window.clearTimeout(this.purchaseTimeout);
      window.clearTimeout(this.cookieDiscoveryTimer);
      this.initialized = false;
    }

    cacheElements() {
      this.stickyButton = this.querySelector('[data-sticky-cart-submit]');
      this.buttonLabel = this.querySelector('[data-sticky-cart-button-label]');
      this.status = this.querySelector('[data-sticky-cart-status]');
    }

    resolvePrimaryButton() {
      this.primaryButton = document.getElementById(this.dataset.primaryButtonId);
      this.primaryProductForm = this.primaryButton?.closest('product-form') || null;
    }

    bindEvents() {
      const options = { signal: this.abortController.signal };
      this.addEventListener('click', this.handleClick, options);
      document.addEventListener('storefront-cart:loading', this.handleCartLoading, options);
      document.addEventListener('storefront-cart:error', this.handleCartError, options);
      document.addEventListener('abok:purchase-result', this.handlePurchaseResult, options);
      document.addEventListener('shopify:block:select', this.handleBlockSelect, options);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect, options);
      this.mediaQuery.addEventListener?.('change', this.handleMediaChange);
    }

    observePrimaryButton() {
      if (!this.primaryButton) return;

      this.primaryStateObserver = new MutationObserver(() => this.syncPrimaryState());
      this.primaryStateObserver.observe(this.primaryButton, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    observeVisibility() {
      if (this.dataset.visibilityMode === 'always' || !this.primaryProductForm) return;

      this.primaryVisibilityObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;

        this.hasPassedPrimary = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.updateVisibility();
      });
      this.primaryVisibilityObserver.observe(this.primaryProductForm);
    }

    observeFooter() {
      if (this.dataset.hideNearFooter !== 'true') return;
      const footer = document.querySelector('footer.footer');
      if (!footer) return;

      this.footerObserver = new IntersectionObserver(
        (entries) => {
          this.footerIsNear = Boolean(entries[0]?.isIntersecting);
          this.updateVisibility();
        },
        { rootMargin: '0px 0px 120px 0px' }
      );
      this.footerObserver.observe(footer);
    }

    observeCookieConsent() {
      if (this.attachCookieConsent()) return;

      this.cookieDiscoveryObserver = new MutationObserver(() => {
        if (!this.attachCookieConsent()) return;

        this.cookieDiscoveryObserver.disconnect();
        window.clearTimeout(this.cookieDiscoveryTimer);
      });
      this.cookieDiscoveryObserver.observe(document.body, { childList: true, subtree: true });
      this.cookieDiscoveryTimer = window.setTimeout(
        () => this.cookieDiscoveryObserver?.disconnect(),
        5000
      );
    }

    attachCookieConsent() {
      const banner = document.querySelector('#shopify-pc__banner');
      if (!banner) return false;

      this.cookieConsent = banner;

      this.cookieObserver = new MutationObserver(() => this.syncCookieState());
      this.cookieObserver.observe(this.cookieConsent, {
        attributes: true,
        attributeFilter: ['class', 'hidden', 'style'],
      });
      this.syncCookieState();
      return true;
    }

    syncCookieState() {
      const styles = this.cookieConsent ? window.getComputedStyle(this.cookieConsent) : null;
      this.cookieIsVisible = Boolean(
        this.cookieConsent &&
          !this.cookieConsent.hidden &&
          styles?.display !== 'none' &&
          styles?.visibility !== 'hidden'
      );
      this.updateVisibility();
    }

    handleVariantChange = (event) => {
      const data = event?.data;
      if (!data || String(data.sectionId) !== String(this.dataset.sectionId)) return;

      const nextComponent = data.html?.getElementById?.(this.id);
      const nextContent = nextComponent?.querySelector('[data-abok-sticky-content]');
      const currentContent = this.querySelector('[data-abok-sticky-content]');
      if (!nextComponent || !nextContent || !currentContent) return;

      currentContent.replaceChildren(...Array.from(nextContent.childNodes, (node) => node.cloneNode(true)));
      this.dataset.variantAvailable = nextComponent.dataset.variantAvailable || 'false';
      this.cacheElements();
      this.syncPrimaryState();
    };

    handleClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-sticky-cart-submit]');
      if (!button || button !== this.stickyButton) return;

      event.preventDefault();
      if (button.disabled || this.isBusy) return;

      this.resolvePrimaryButton();
      if (!this.primaryButton || this.primaryButton.disabled) {
        this.syncPrimaryState();
        return;
      }

      this.setStatus('');
      this.awaitingPurchase = true;
      this.setBusy(true);
      this.primaryButton.click();
      document.querySelector('cart-drawer')?.setActiveElement?.(button);

      queueMicrotask(() => {
        if (!this.primaryButton?.classList.contains('loading')) this.finishPurchase();
      });
      window.clearTimeout(this.purchaseTimeout);
      this.purchaseTimeout = window.setTimeout(() => this.finishPurchase(), 30000);
    };

    handleCartLoading = (event) => {
      if (!this.awaitingPurchase) return;
      if (event.detail?.loading) this.setBusy(true);
    };

    handleCartError = (event) => {
      if (!this.awaitingPurchase) return;
      this.setStatus(event.detail?.message || window.cartStrings?.error || '');
    };

    handlePurchaseResult = (event) => {
      if (!this.awaitingPurchase || !(event.target instanceof Element)) return;
      if (event.target.closest('product-info') !== this.closest('product-info')) return;

      if (event.detail?.status === 'failure' && !this.status?.textContent) {
        this.setStatus(window.cartStrings?.error || '');
      }
      this.finishPurchase();
    };

    handleBlockSelect = (event) => {
      if (!(event.target instanceof Element) || event.target.closest(elementName) !== this) return;
      this.isEditorForced = true;
      this.updateVisibility();
    };

    handleBlockDeselect = (event) => {
      if (!(event.target instanceof Element) || event.target.closest(elementName) !== this) return;
      this.isEditorForced = false;
      this.updateVisibility();
    };

    handleMediaChange = () => this.updateVisibility();

    setBusy(isBusy) {
      this.isBusy = isBusy;
      this.toggleAttribute('aria-busy', isBusy);
      this.stickyButton?.classList.toggle('loading', isBusy);
      this.stickyButton?.querySelector('.loading__spinner')?.classList.toggle('hidden', !isBusy);
      this.syncPrimaryState();
    }

    finishPurchase() {
      this.awaitingPurchase = false;
      window.clearTimeout(this.purchaseTimeout);
      this.setBusy(false);
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message || '';
    }

    getPrimaryLabel() {
      return this.primaryButton?.querySelector('span')?.textContent?.trim() || '';
    }

    syncPrimaryState() {
      if (!this.stickyButton) return;
      if (!this.primaryButton?.isConnected) this.resolvePrimaryButton();

      const variantAvailable = this.dataset.variantAvailable === 'true';
      const primaryDisabled = !this.primaryButton || this.primaryButton.disabled;
      const unavailable = !variantAvailable || primaryDisabled;
      const disabled = unavailable || this.isBusy;
      const label = unavailable
        ? this.getPrimaryLabel() || this.stickyButton.dataset.stateLabel
        : this.stickyButton.dataset.availableLabel;

      this.stickyButton.disabled = disabled;
      this.stickyButton.toggleAttribute('aria-disabled', disabled);
      if (this.buttonLabel && label) this.buttonLabel.textContent = label;
      if (label) {
        const title = this.stickyButton.dataset.productTitle || '';
        this.stickyButton.setAttribute('aria-label', title ? `${label} - ${title}` : label);
      }
    }

    updateVisibility() {
      const isMobile = this.mediaQuery?.matches;
      const deviceAllowed = isMobile ? this.dataset.showMobile === 'true' : this.dataset.showDesktop === 'true';
      const enabled = this.dataset.enabled === 'true';
      const normalVisibility = enabled && deviceAllowed && this.hasPassedPrimary && !this.footerIsNear;
      const visible = !this.cookieIsVisible && Boolean(this.primaryButton) && (this.isEditorForced || normalVisibility);

      this.dataset.visible = String(visible);
      this.setAttribute('aria-hidden', String(!visible));
      this.inert = !visible;
    }
  }

  customElements.define(elementName, AbokStickyAddToCart);
})();
